import { FC } from 'react';
import cn from 'classnames';
import { Builder } from '@builder.io/react';

import { SharedBuilderComponent } from '@/shared/types';
import { getBuilderIOClassName } from '@/shared/utils/getBuilderIOClassName';
import { getBuilderSpacingClass } from '@/shared/spacing';
import { backgroundColorInput, componentLinkIdInput } from '@/shared/inputs';
import { COLOR_NAME_WHITE } from '@/shared/constants/colors';

import { Topic, topicInput } from './Topic';
import sharedStyle from '../shared.module.scss';
import styles from './DuoContent.module.scss';

interface BuilderInput extends SharedBuilderComponent {
  componentLinkId?: string;
  leftTopic: Topic;
  rightTopic: Topic;
}

interface Props extends BuilderInput {
  locale: string;
}

export const DuoContents: FC<Props> = ({
  attributes,
  backgroundColorClassName,
  builderBlock,
  builderState,
  componentLinkId,

  leftTopic,
  rightTopic,

  locale,
}) => {
  const builderStyles = getBuilderIOClassName(attributes);
  const spacingStyles = getBuilderSpacingClass(builderBlock, builderState);

  // When open page in editor or LivePreview mode, Builder doesn't run dataFetcher
  // causing locale value undefined. So, we use builderState locale value when
  // using component in editor and leave the locale when we generate static content
  const blockLocale = locale || builderState?.context.locale || 'en';

  return (
    <section
      id={componentLinkId}
      {...attributes}
      className={cn(
        sharedStyle.section,
        builderStyles,
        spacingStyles,
        backgroundColorClassName
      )}
    >
      <div className={cn('u-wrapper')}>
        <div className={styles.grid}>
          <Topic className={styles.left} {...leftTopic} locale={blockLocale} />
          <Topic
            className={styles.right}
            {...rightTopic}
            locale={blockLocale}
          />
        </div>
      </div>
    </section>
  );
};

export function dataFetcher(locale: string) {
  return async () => ({
    locale,
  });
}

export const NAME = 'DuoContents';

export function componentRegister() {
  Builder.registerComponent(DuoContents, {
    name: NAME,
    inputs: [
      componentLinkIdInput(),
      backgroundColorInput(COLOR_NAME_WHITE),
      topicInput('leftTopic', 'Left Topic'),
      topicInput('rightTopic', 'Right Topic'),
    ],
    noWrap: true,
  });
  return NAME;
}

export const componentDataFetchers = (locale: string) => ({
  [NAME]: dataFetcher(locale),
});
